import classNames from "classnames";
import type { WheelEvent } from "react";
import { AiPipelineElapsedTimer } from "./AiPipelineElapsedTimer";
import { AiPipelineIndeterminateProgress } from "./AiPipelineIndeterminateProgress";
import { aiPipelineAccentStyles } from "./aiPipelineAccentStyles";
import { AiPipelineRunningVisual } from "./AiPipelineRunningVisual";
import { AiPipelineStepList } from "./AiPipelineStepList";
import type { AiPipelineRunningPanelProps } from "./AiPipelineRunningPanel.types";
import { useAiFeatureRunState } from "./useAiFeatureRunState";
import { usePageOverlayInsetClass } from "./usePageOverlayInsetClass";
import { usePageScrollLock } from "./usePageScrollLock";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

const OVERLAY_BACKDROP_CLASS =
  "flex flex-col items-center justify-center gap-3 bg-background/65 p-6 backdrop-blur-sm";

const AiPipelineRunningCard = ({
  title,
  pausedHint,
  steps,
  activeStepIndex,
  activeStepActivityHint,
  stepCount,
  estimatedProgressPercent,
  durationHint,
  longRunHint,
  showLongRunHint,
  elapsedMs,
  accentKey,
  visual,
  reducedMotion,
}: {
  title: string;
  pausedHint: string;
  steps: string[];
  activeStepIndex: number;
  activeStepActivityHint: string;
  stepCount: number;
  estimatedProgressPercent: number;
  durationHint: string;
  longRunHint: string;
  showLongRunHint: boolean;
  elapsedMs: number;
  accentKey: keyof typeof aiPipelineAccentStyles;
  visual: Parameters<typeof AiPipelineRunningVisual>[0]["variant"];
  reducedMotion: boolean;
}) => {
  const accent = aiPipelineAccentStyles[accentKey];

  return (
    <div
      className={classNames(
        "flex w-full max-w-md flex-col gap-3 rounded-xl border bg-card p-5 text-card-foreground shadow-xl shadow-black/10 dark:shadow-2xl dark:shadow-black/40",
        accent.cardBorder
      )}
      style={{
        backgroundColor: "rgb(var(--card))",
        color: "rgb(var(--card-foreground))",
      }}
    >
      <div className="flex items-start gap-4">
        <AiPipelineRunningVisual
          variant={visual}
          accent={accent}
          activeStepIndex={activeStepIndex}
          stepCount={stepCount}
          reducedMotion={reducedMotion}
          size="md"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
          <h2 className={`text-base font-semibold ${accent.titleText}`}>{title}</h2>
          <p className={`text-sm ${accent.mutedText}`}>{pausedHint}</p>
        </div>
      </div>

      <AiPipelineIndeterminateProgress
        accent={accent}
        reducedMotion={reducedMotion}
        estimatedProgressPercent={estimatedProgressPercent}
      />

      <div className={`flex items-center justify-between gap-3 text-xs ${accent.mutedText}`}>
        <span>{durationHint}</span>
        <span className="tabular-nums">
          <AiPipelineElapsedTimer elapsedMs={elapsedMs} />
        </span>
      </div>

      <AiPipelineStepList
        steps={steps}
        activeStepIndex={activeStepIndex}
        activeStepActivityHint={activeStepActivityHint}
        accent={accent}
        reducedMotion={reducedMotion}
      />

      {showLongRunHint ? <p className={`text-xs ${accent.mutedText}`}>{longRunHint}</p> : null}
    </div>
  );
};

export const AiPipelineRunningPanel = ({
  pipeline,
  layout,
  featureStatuses,
  projectId = "",
  isRunningOverride,
  className = "",
  footer,
  hasExistingData = false,
}: AiPipelineRunningPanelProps) => {
  const reducedMotion = usePrefersReducedMotion();
  const {
    config,
    isRunning,
    activeStepIndex,
    activeStepLabel,
    activeStepActivityHint,
    stepNumber,
    stepCount,
    estimatedProgressPercent,
    elapsedMs,
    showLongRunHint,
  } = useAiFeatureRunState({
    pipeline,
    featureStatuses,
    projectId,
    isRunningOverride,
  });

  const isFullOverlay = layout === "page-overlay" || layout === "panel-overlay";
  // Panels that already have data get the soft banner treatment: readable
  // content behind a slim banner instead of a blurred cover, and no scroll lock.
  const softMode = isFullOverlay && hasExistingData;
  const locksPageScroll = isRunning && isFullOverlay && !softMode;
  const pageOverlayInsetClass = usePageOverlayInsetClass();
  usePageScrollLock(locksPageScroll);

  if (!isRunning) {
    return null;
  }

  const accent = aiPipelineAccentStyles[config.accent];

  const banner = (
    <div
      className={classNames(
        "flex flex-col gap-2 rounded-md border px-4 py-3 shadow-md sm:flex-row sm:items-center sm:justify-between",
        accent.bannerBorder,
        accent.bannerBg
      )}
      style={{ backgroundColor: "rgb(var(--card))" }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <AiPipelineRunningVisual
          variant={config.visual}
          accent={accent}
          activeStepIndex={activeStepIndex}
          stepCount={stepCount}
          reducedMotion={reducedMotion}
          size="sm"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={`text-sm font-medium ${accent.bannerText}`}>{config.title}</span>
          <span className={`text-xs ${accent.mutedText}`}>
            Step {stepNumber} of {stepCount}: {activeStepLabel} · {config.pausedHint}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3 text-xs">
        <span className={`tabular-nums font-medium ${accent.bannerText}`}>
          <AiPipelineElapsedTimer elapsedMs={elapsedMs} />
        </span>
      </div>
      {footer}
    </div>
  );

  if (softMode) {
    // Best-effort wheel forwarding: the veil blocks edits while the run merges
    // results, but reading should still include scrolling the page container.
    const forwardWheel = (event: WheelEvent<HTMLDivElement>) => {
      const scroller =
        document.querySelector<HTMLElement>("[data-panel-scroll]") ??
        document.querySelector<HTMLElement>("[data-page-scroll]");
      if (scroller) {
        scroller.scrollTop += event.deltaY;
        scroller.scrollLeft += event.deltaX;
      }
    };

    return (
      <div
        className={classNames(
          "pointer-events-none fixed bottom-0 right-0 top-[54px] z-40 flex flex-col gap-0 p-3",
          pageOverlayInsetClass,
          className
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="pointer-events-auto">{banner}</div>
        <div
          className="pointer-events-auto flex-1 cursor-default"
          onWheel={forwardWheel}
          aria-hidden
        />
      </div>
    );
  }

  if (layout === "page-banner") {
    return (
      <div
        className={classNames(
          "flex flex-col gap-2 rounded-md border px-4 py-3 sm:flex-row sm:items-center sm:justify-between",
          accent.bannerBorder,
          accent.bannerBg,
          className
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <AiPipelineRunningVisual
            variant={config.visual}
            accent={accent}
            activeStepIndex={activeStepIndex}
            stepCount={stepCount}
            reducedMotion={reducedMotion}
            size="sm"
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className={`text-sm font-medium ${accent.bannerText}`}>{config.title}</span>
            <span className={`text-xs ${accent.mutedText}`}>
              Step {stepNumber} of {stepCount}: {activeStepLabel}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-xs">
          <span className={`tabular-nums font-medium ${accent.bannerText}`}>
            <AiPipelineElapsedTimer elapsedMs={elapsedMs} />
          </span>
        </div>
        {footer}
      </div>
    );
  }

  const card = (
    <AiPipelineRunningCard
      title={config.title}
      pausedHint={config.pausedHint}
      steps={config.steps}
      activeStepIndex={activeStepIndex}
      activeStepActivityHint={activeStepActivityHint}
      stepCount={stepCount}
      estimatedProgressPercent={estimatedProgressPercent}
      durationHint={config.durationHint}
      longRunHint={config.longRunHint}
      showLongRunHint={showLongRunHint}
      elapsedMs={elapsedMs}
      accentKey={config.accent}
      visual={config.visual}
      reducedMotion={reducedMotion}
    />
  );

  if (layout === "editor-overlay") {
    return (
      <div
        className={classNames(
          "absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/60 p-6 backdrop-blur-sm",
          className
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {card}
        {footer}
      </div>
    );
  }

  if (layout === "panel-overlay" || layout === "page-overlay") {
    return (
      <div
        className={classNames(
          "fixed bottom-0 right-0 top-[54px] z-50",
          pageOverlayInsetClass,
          OVERLAY_BACKDROP_CLASS,
          className
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {card}
        {footer}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "flex min-h-[min(480px,60vh)] w-full flex-col items-center justify-center p-8",
        className
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {card}
      {footer}
    </div>
  );
};
